// Carry Roll ResultsBlock — hero total, carry/roll split, measured/playing, apex, descent, shape, profile.

import { useStore } from '../../store';
import { useResult, useSplitDisplay } from '../../store/selectors';
import { DistanceMode } from '@carry-roll/shared-types';
import { BenchmarkOverlay } from './BenchmarkOverlay';
import { HazardCheck } from './HazardCheck';
import { ObstacleCheck } from './ObstacleCheck';
import { HelpTooltip } from './HelpTooltip';

export function ResultsBlock() {
  const result = useResult();
  const split = useSplitDisplay();
  const distanceUnit = useStore((s) => s.distanceUnit);
  const distanceMode = useStore((s) => s.distanceMode);
  const validationCount = useStore((s) => s.result.warnings.filter((w) => w.code !== 'info').length);

  const dimmed = validationCount > 0;
  const heroDisplay = distanceMode === DistanceMode.Carry ? split.carryDisplay : split.totalDisplay;
  const heroLabel = distanceMode === DistanceMode.Carry ? 'Carry' : 'Total';

  return (
    <div
      className="panel"
      data-testid="results-block"
      style={dimmed ? { opacity: 0.85 } : undefined}
      aria-label={`Carry ${split.carryDisplay}, roll ${split.rollDisplay}, total ${split.totalDisplay}`}
    >
      {/* Hero */}
      <div style={{ textAlign: 'center' }}>
        <div
          className="text-xs"
          style={{
            color: 'var(--color-text-muted)',
            textTransform: 'uppercase',
            letterSpacing: '0.18em',
            fontWeight: 'var(--fw-medium)',
          }}
        >
          {heroLabel}
        </div>
        <div
          data-testid="results-total"
          className="cr-hero"
          style={{
            fontSize: 'var(--fs-display)',
            fontWeight: 'var(--fw-bold)',
            color: distanceMode === DistanceMode.Carry ? 'var(--color-carry)' : 'var(--color-total)',
            lineHeight: 'var(--lh-tight)',
            marginTop: 'var(--space-1)',
          }}
        >
          {heroDisplay}
        </div>
      </div>

      {/* Carry + roll contributions */}
      <div className="mt-4 grid grid-cols-2 gap-3">
        <Contribution label="Carry" value={split.carryDisplay} color="var(--color-carry)" shape="●" testId="results-carry" />
        <Contribution label="Roll" value={split.rollDisplay} color="var(--color-roll)" shape="▼" testId="results-roll" />
      </div>

      {/* % split bar */}
      <div className="mt-3">
        <div className="split-bar" role="img" aria-label={`Carry ${Math.round(split.carryPercent * 100)} percent, roll ${Math.round(split.rollPercent * 100)} percent`}>
          <div className="split-bar-carry" style={{ width: `${split.carryPercent * 100}%` }} />
          <div className="split-bar-roll" style={{ width: `${split.rollPercent * 100}%` }} />
        </div>
        <div className="mt-1 flex justify-between text-xs" style={{ color: 'var(--color-text-muted)' }}>
          <span>● {Math.round(split.carryPercent * 100)}%</span>
          <span>▼ {Math.round(split.rollPercent * 100)}%</span>
        </div>
      </div>

      {/* Measured vs playing (1.5) */}
      <div className="mt-4 flex justify-between items-center">
        <div>
          <div className="text-xs" style={{ color: 'var(--color-measured)' }}>
            ● Measured
            <HelpTooltip label="Measured distance">Total distance recomputed at sea level with no wind — a neutral reference for comparing shots.</HelpTooltip>
          </div>
          <div className="text-lg font-medium" style={{ color: 'var(--color-measured)' }}>
            {split.measuredDisplay}
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div className="text-xs" style={{ color: 'var(--color-total)' }}>
            ▼ Playing (total)
          </div>
          <div className="text-lg font-medium" style={{ color: 'var(--color-total)' }}>
            {split.totalDisplay}
          </div>
        </div>
      </div>

      {/* Apex + descent + shape + profile */}
      <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
        <Stat label="Apex" value={split.apexDisplay} help="Peak height of the ball in flight, in your height unit." />
        <Stat label="Descent" value={`${result.landingAngleDeg.toFixed(0)}°`} help="Angle of the ball's path at landing, in degrees. Steeper angles stop faster." />
        <Stat label="Shot shape" value={result.shotShape} help="Classified from spin-axis tilt: straight, draw/fade, or hook/slice." />
        <Stat label="Profile" value={result.profile} help="Trajectory class from apex-to-carry ratio: low, mid, or high." />
      </div>

      <div className="mt-3">
        <BenchmarkOverlay />
        <HazardCheck />
        <div className="mt-1">
          <ObstacleCheck />
        </div>
      </div>
      <span style={{ display: 'none' }} data-testid="distance-unit" aria-hidden="true">
        {distanceUnit}
      </span>
    </div>
  );
}

function Contribution({
  label,
  value,
  color,
  shape,
  testId,
}: {
  label: string;
  value: string;
  color: string;
  shape: string;
  testId: string;
}) {
  return (
    <div data-testid={testId}>
      <div className="text-xs" style={{ color }}>
        {shape} {label}
      </div>
      <div className="text-xl font-medium" style={{ color }}>
        {value}
      </div>
    </div>
  );
}

function Stat({ label, value, help }: { label: string; value: string; help?: string }) {
  return (
    <div>
      <div className="field-label">
        {label}
        {help != null && <HelpTooltip label={label}>{help}</HelpTooltip>}
      </div>
      <div className="font-medium">
        {value}
      </div>
    </div>
  );
}
